import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

interface SwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'type' | 'value'> {
  checked: boolean
  onCheckedChange: (next: boolean) => void
  size?: 'sm' | 'md'
  label?: string
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onCheckedChange, size = 'md', label, className, disabled, ...rest }, ref) => {
    const dims =
      size === 'sm'
        ? { track: 'h-4 w-7', thumb: 'h-3 w-3', shift: 'translate-x-3' }
        : { track: 'h-5 w-9', thumb: 'h-4 w-4', shift: 'translate-x-4' }

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation()
          if (!disabled) onCheckedChange(!checked)
        }}
        className={cn(
          'inline-flex shrink-0 items-center rounded-pill border transition-colors duration-fast ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
          dims.track,
          checked
            ? 'bg-brand-600 border-brand-600 hover:bg-brand-700'
            : 'bg-neutral-200 border-neutral-200 hover:bg-neutral-300',
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
        {...rest}
      >
        <span
          className={cn(
            'inline-block bg-white rounded-pill shadow-sm transition-transform duration-fast ease-out',
            dims.thumb,
            checked ? dims.shift : 'translate-x-0.5',
          )}
        />
      </button>
    )
  },
)
Switch.displayName = 'Switch'
